import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Table, Button, Space, Tag, message, Avatar, Select } from "antd";
import { PlusOutlined, UserOutlined } from "@ant-design/icons";
import Layout from "../../Layout";
import AddEmployeeModal from "./AddModal";
import {
    Edit,
    X,
} from "lucide-react";
import { mixDate, mixDelV1, mixGetV1, mixPostV1, mixPostV2, mixPutV1, mixPutV2 } from "services/mixin";
import { getRoles, type RoleItem } from "../../api/Role";
import useAuthStore from "../../store/authStore";
import useTableColumnSettings, { SettingColumn } from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";

interface StaffItem {
    id: number;
    cms_id: string;
    name: string;
    email: string;
    phone: string;
    position: string;
    avatar?: string;
    department_id: number | null;
    role_id: number | null;
    status: number;
    created_at: string | null;
    updated_at: string | null;
    deleted_at: string | null;
    department?: {
        id: number;
        name: string;
    };
    role?: {
        id: number;
        name: string;
    };
    user?: {
        id?: number;
        email?: string;
    };
}

export default function EmployeePage() {
    const { t } = useTranslation();
    // Quyền thao tác trên nhân viên: chỉ có employee.view thì xem, không thấy nút thêm/sửa/xóa
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canCreate = isSuperAdmin || hasPermission("employee.create");
    const canEdit = isSuperAdmin || hasPermission("employee.edit");
    const canDelete = isSuperAdmin || hasPermission("employee.delete");
    const [searchTerm, setSearchTerm] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState<"add" | "edit">("add");
    const [editingItem, setEditingItem] = useState<StaffItem | null>(null);

    const [data, setData] = useState<StaffItem[]>([]);
    // Lọc nhân sự theo phòng ban (query department_id của API /be/staffs)
    const [departmentId, setDepartmentId] = useState<number | null>(null);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [sortParam, setSortParam] = useState<string>("id,desc");

    const [departments, setDepartments] = useState<{ id: number; name: string }[]>([]);
    const [roles, setRoles] = useState<{ id: number; name: string }[]>([]);
    const [submitLoading, setSubmitLoading] = useState(false);

    const [actionModal, setActionModal] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });

    const openDeleteModal = (id: number) => {
        setActionModal({ open: true, id });
    };

    const handleCloseActionModal = () => {
        setActionModal({ open: false, id: null });
    };

    const handleConfirmAction = async () => {
        if (actionModal.id !== null) {
            setSubmitLoading(true);
            const res = await mixDelV1(`/api/v1/be/staffs/${actionModal.id}`, { _method: "DELETE" });

            if (res.status) {
                message.success(t('employee.delete_success'));
                getList();
            } else {
                message.error(t('employee.delete_failed'));
            }
            setSubmitLoading(false);
        }
        handleCloseActionModal();
    };

    const openAdd = () => {
        setMode("add");
        setEditingItem(null);
        setModalOpen(true);
    };

    const openEdit = (record: StaffItem) => {
        setMode("edit");
        setEditingItem(record);
        setModalOpen(true);
    };

    const handleSubmit = async (values: any) => {
        setSubmitLoading(true);
        const param: any = {
            ...values,
            status: values.is_active ? 0 : 1,
        };
        delete param.is_active;

        if (!param.email_user) {
            delete param.email_user;
            delete param.password_user;
        } else if (!param.password_user) {
            delete param.password_user;
        }

        // Có chọn avatar mới -> gửi multipart (FormData) qua helper V2.
        const hasAvatar = param.avatar instanceof File;
        if (!hasAvatar) {
            delete param.avatar;
        } else {
            // FormData biến null/undefined thành chuỗi "null"/"undefined" -> loại bỏ trước.
            Object.keys(param).forEach((k) => {
                if (param[k] === null || param[k] === undefined) delete param[k];
            });
        }

        if (mode === "add") {
            const res = hasAvatar
                ? await mixPostV2('/api/v1/be/staffs', param)
                : await mixPostV1('/api/v1/be/staffs', param);

            if (res.status) {
                message.success(t('employee.add_success'));
                getList();
            } else {
                message.error(t('employee.add_failed'));
            }
        } else if (mode === "edit" && editingItem) {
            const res = hasAvatar
                ? await mixPutV2(`/api/v1/be/staffs/${editingItem.id}`, param)
                : await mixPutV1(`/api/v1/be/staffs/${editingItem.id}`, param);

            if (res.status) {
                message.success(t('employee.edit_success'));
                getList();
            } else {
                message.error(t('employee.edit_failed'));
            }
        }

        setSubmitLoading(false);
        setModalOpen(false);
    };

    const columns: SettingColumn<StaffItem>[] = [
        {
            title: "Id",
            dataIndex: "id",
            key: "id",
            sorter: true,
        },
        {
            title: "CMS ID",
            dataIndex: "cms_id",
            key: "cms_id",
        },
        {
            title: t('employee.col_employee'),
            dataIndex: "name",
            key: "name",
            sorter: true,
            render: (text: string, record) => (
                <div className="flex items-center gap-2">
                    <Avatar src={record.avatar || undefined} icon={<UserOutlined />} />
                    <span className="font-medium">{text}</span>
                </div>
            ),
        },
        {
            title: t('employee.col_account'),
            dataIndex: "account",
            render: (_, record: any) => (
                <span className="font-medium">{record.user?.email}</span>
            ),
        },
        {
            title: t('employee.col_department'),
            key: "department",
            render: (_, record) => record.department?.name || "-",
        },
        {
            title: "Nhóm quyền",
            key: "role",
            render: (_, record) => record.role?.name || "-",
        },
        {
            title: t('employee.col_status'),
            key: "status",
            render: (_, record) => (
                <Tag color={record.status === 0 ? "green" : "red"}>
                    {record.status === 0 ? t('employee.status_active') : t('employee.status_locked')}
                </Tag>
            ),
        },
        // Không có quyền sửa lẫn xóa -> bỏ hẳn cột Hành động
        ...(canEdit || canDelete ? [{
            key: "actions",
            title: t('employee.col_action'),
            fixed: "right" as const,
            lockVisible: true,
            render: (_: any, record: StaffItem) => (
                <div className="flex items-center gap-3">
                    <div className="flex items-center space-x-3 gap-2">
                        {canEdit && (
                            <button
                                onClick={() => openEdit(record)}
                                className="!text-[#3e91ab] hover:text-yellow-700 bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50 p-1.5 rounded-md transition-colors"
                                title={t('employee.btn_edit')}
                            >
                                <Edit className="h-4 w-4" />
                            </button>
                        )}
                        {canDelete && (
                            <button
                                onClick={() => openDeleteModal(record.id)}
                                className="!text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 p-1.5 rounded-md transition-colors"
                                title={t('employee.btn_delete')}
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>
            ),
        }] : []),
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings("employee_list", columns);

    // --------------- API ------------------------
    const getList = async () => {
        setLoading(true);

        const param = {
            page,
            search: searchTerm,
            sort: sortParam,
            department_id: departmentId ?? undefined,
        };

        const res = await mixGetV1('/api/v1/be/staffs', param);

        if (res.status) {
            setData(res.data.items);
            setTotal(res.data.total);
        }
        setLoading(false);
    };

    const getDepartments = async () => {
        const res = await mixGetV1('/api/v1/be/departments', {});
        if (res.status) {
            setDepartments(res.data.items || res.data);
        }
    };

    const fetchRoles = async () => {
        const res = await getRoles({ take: 200 });
        if (!res.status) return;
        const raw = res.data;
        const items: RoleItem[] = Array.isArray(raw)
            ? raw
            : Array.isArray(raw?.items)
            ? raw!.items!
            : [];
        setRoles(items.map(r => ({ id: r.id, name: r.name })));
    };

    useEffect(() => {
        getDepartments();
        fetchRoles();
    }, []);

    useEffect(() => {
        getList();
    }, [page, searchTerm, sortParam, departmentId]);

    return (
        <Layout>
            <Helmet>
                <title>{t('page.employees')} | Khothi</title>
            </Helmet>
            <div className="">
                <div className="flex gap-2 flex-col md:flex-row md:justify-between mb-4">
                    <h2 className="text-lg text-gray-900 font-semibold t-text-white">
                        {t('employee.title')}
                    </h2>

                    <Space>
                        {/* Lọc theo phòng ban */}
                        <Select
                            allowClear
                            showSearch
                            placeholder={t('employee.filter_department')}
                            className="min-w-[180px]"
                            value={departmentId ?? undefined}
                            onChange={(val) => {
                                setDepartmentId(val ?? null);
                                setPage(1);
                            }}
                            options={departments.map((d) => ({ value: d.id, label: d.name }))}
                            optionFilterProp="label"
                        />
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-search h-5 w-5 text-gray-400" aria-hidden="true"><path d="m21 21-4.34-4.34"></path><circle cx="11" cy="11" r="8"></circle></svg>
                            </div>
                            <input
                                type="text"
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setPage(1);
                                }}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 t-text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#3e91ab] focus:border-[#3e91ab] sm:text-sm transition-colors h-[32px]"
                                placeholder={t('employee.search')}
                            />
                        </div>
                        {canCreate && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openAdd}
                                className="!bg-[#3e91ab] hover:!bg-[#357f96] border-none"
                            >
                                {t('employee.add')}
                            </Button>
                        )}
                        <ColumnSettingButton {...columnSettingProps} />
                    </Space>
                </div>

                <Table
                    loading={loading}
                    rowKey="id"
                    columns={tableColumns}
                    components={tableComponents}
                    tableLayout={tableLayout}
                    dataSource={data}
                    scroll={{ x: scrollX }}
                    className="custom-ant-table"
                    onChange={(pagination, _filters, sorter: any) => {
                        if (sorter?.field && sorter?.order) {
                            const direction = sorter.order === 'ascend' ? 'asc' : 'desc';
                            setSortParam(`${sorter.field},${direction}`);
                            setPage(1);
                        } else if (sorter?.columnKey && sorter?.order) {
                            const direction = sorter.order === 'ascend' ? 'asc' : 'desc';
                            setSortParam(`${sorter.columnKey},${direction}`);
                            setPage(1);
                        } else if (sorter && Object.keys(sorter).length > 0) {
                            setSortParam('id,desc');
                            setPage(1);
                        } else {
                            setPage(pagination.current ?? 1);
                        }
                    }}
                    pagination={{
                        current: page,
                        total: total,
                        pageSize: 10,
                    }}
                />

                <AddEmployeeModal
                    open={modalOpen}
                    mode={mode}
                    initialValues={
                        editingItem
                            ? {
                                name: editingItem.name,
                                cms_id: editingItem.cms_id,
                                email: editingItem.email,
                                phone: editingItem.phone,
                                position: editingItem.position,
                                department_id: editingItem.department_id,
                                role_id: editingItem.role_id ?? editingItem.role?.id ?? null,
                                is_active: editingItem.status === 0,
                                email_user: editingItem.user?.email ?? "",
                                password_user: "",
                            }
                            : undefined
                    }
                    initialAvatar={editingItem?.avatar}
                    departments={departments}
                    roles={roles}
                    onCancel={() => setModalOpen(false)}
                    onSubmit={handleSubmit}
                    loading={submitLoading}
                />

                {/* Delete Confirmation Modal */}
                {actionModal.open && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-gray-800 rounded-md shadow-xl w-full max-w-md">
                            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="text-lg font-bold text-gray-900 t-text-white">
                                    {t('employee.delete_title')}
                                </h3>
                                <p className="text-sm text-gray-500 text-000000e0 mt-1">
                                    {t('employee.delete_confirm')}
                                </p>
                            </div>
                            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                                <button
                                    onClick={handleCloseActionModal}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg !text-gray-700 dark:!text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                                >
                                    {t('employee.delete_cancel')}
                                </button>
                                <button
                                    onClick={handleConfirmAction}
                                    disabled={submitLoading}
                                    className="px-4 py-2 !text-white rounded-lg transition bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {submitLoading ? '...' : t('employee.delete_ok')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}